import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Store from '@apply/store';
import { Footer } from '../common_v2/components/Footer';
import { alertModal } from '@apply/ui';
import { Button, ButtonTheme, EmailBox, Panel, PanelTheme, PanelBorderRadius } from '@apply/components';
import { isMobileScreen } from '@apply/ui';
import { LogoLink } from '../common_v2/components/LogoLink';

export interface Actions {
    forgotPassword?: (email: string) => Promise<boolean>;
}

export interface ForgotPasswordPageV2Props {
    actions?: Actions;
}

export const ForgotPasswordPageV2: React.FC<ForgotPasswordPageV2Props> = ({ actions }) => {
    const navigate = useNavigate();
    const mobileScreen = isMobileScreen();
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const requestedEmail = queryParams.get('email');

    const [email, setEmail] = useState<string>(requestedEmail ?? '');
    const [isEmailValid, set_isEmailValid] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [successful, setSuccessful] = useState<boolean>(false);

    const mounted = useRef<boolean>(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const handleRequestForgotPassword = async () => {
        /* istanbul ignore if */
        if (!email) {
            return;
        }

        setSubmitting(true);

        try {
            const result = await actions.forgotPassword(email);
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                if (result) {
                    setSuccessful(true);
                } else {
                    showError();
                }
            }
        } catch (err) {
            setSubmitting(false);
            setSuccessful(false);
            showError(err.message);
        }
    };

    const showError = (msg?: string | null | undefined) => {
        alertModal({
            message: msg ?? 'An error has occurred while communicating with our server.  Please try your operation again.',
        });
    };

    const readyToSubmit = () => {
        return !submitting && isEmailValid;
    };

    const onEnterKey = () => {
        // only submit if both fields valid
        if (!submitting && readyToSubmit()) {
            handleRequestForgotPassword();
        }
    };

    const onChangeEmail = (email: string) => {
        setEmail(email);
        setSubmitting(false);
    };

    const renderPasswordResetContent = () => {
        return (
            <>
                <div className={`preAuthenticationLogoLink ${isMobileScreen() ? 'mobile' : ''}`}>
                    <LogoLink />
                </div>
                <main>
                    <h2>Request a password reset</h2>
                    <div className='passwordReset subheader'>
                        Please enter your email address.
                        <br />
                        We will send you an email to reset your password.
                    </div>
                    <EmailBox
                        id='emailAddress'
                        dataTestid='forgot-password-email-address'
                        name='email'
                        value={email}
                        onChange={onChangeEmail}
                        onEnterKey={onEnterKey}
                        onValidationEvent={(isValid) => set_isEmailValid(isValid)}
                        label='Email Address'
                        required={true}
                    />
                    <div className='passwordReset primaryActions'>
                        <Button
                            id='forgot-password-button'
                            dataTestid='forgot-password-button'
                            key='forgot-password-button'
                            width='80%'
                            theme={ButtonTheme.Primary}
                            disabled={!readyToSubmit()}
                            handleClick={handleRequestForgotPassword}
                        >
                            Reset Password
                        </Button>
                    </div>
                    <div className='passwordReset secondaryActions'>
                        <div style={{ width: '100%' }}>
                            <Button
                                id='register-button'
                                dataTestid='navigate-register-button'
                                key='register-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={(e) => navigate('/register')}
                                width='100px'
                            >
                                Register
                            </Button>
                        </div>
                        <div style={{ float: 'right', width: 'auto' }}>
                            <Button
                                id='login-button'
                                dataTestid='navigate-login-button'
                                key='login-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={(e) => navigate('/login')}
                                width='100px'
                            >
                                Log In
                            </Button>
                        </div>
                    </div>
                </main>
            </>
        );
    };

    const renderSuccessContent = () => {
        return (
            <>
                <main>
                    <div style={{ textAlign: 'center' }}>
                        <h2>Your request has been processed</h2>
                        <div style={{ paddingTop: '2vh' }}>Please check your email for the next steps...</div>
                    </div>
                </main>
            </>
        );
    };

    if (successful) {
        return (
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    {!mobileScreen ? (
                        <div className='authenticationContainer-desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width='90%'
                            >
                                {renderSuccessContent()}
                            </Panel>
                        </div>
                    ) : (
                        <div className='authenticationContainer mobile'>{renderSuccessContent()}</div>
                    )}
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <>
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    {!mobileScreen ? (
                        <div className='authenticationContainer-desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width='90%'
                            >
                                <div className='authenticationContainer-desktop-desktopWrapper'>
                                    {renderPasswordResetContent()}
                                </div>
                            </Panel>
                        </div>
                    ) : (
                        <div className='authenticationContainer mobile'>{renderPasswordResetContent()}</div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            forgotPassword: (email: string) => dispatch(Store.CrossStore.forgotPassword(email)),
        },
        dispatch,
    };
    return props;
};

export default connect(null, mapDispatchToProps)(ForgotPasswordPageV2);
